import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { translateRoomType } from '../../../constants/roomTypes';

const ReservationTable = ({ reservations = [], onEdit, onViewDetails, onCheckIn, onCheckOut, onProcessPayment, onCancel, onDelete, loadingStates = {} }) => {
  const [selectedReservations, setSelectedReservations] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });


  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'bg-success text-success-foreground';
      case 'pending': return 'bg-warning text-warning-foreground';
      case 'cancelled': return 'bg-destructive text-destructive-foreground';
      case 'checked_in': return 'bg-primary text-primary-foreground';
      case 'checked_out': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'bg-success text-success-foreground';
      case 'pending': return 'bg-warning text-warning-foreground';
      case 'overdue': return 'bg-destructive text-destructive-foreground';
      case 'cancelled': return 'bg-muted text-muted-foreground';
      case 'refunded': return 'bg-info text-info-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'PENDING': 'Pendente',
      'CONFIRMED': 'Confirmada',
      'CHECKED_IN': 'Check-in Realizado',
      'CHECKED_OUT': 'Check-out Realizado',
      'CANCELLED': 'Cancelada'
    };
    return labels[status] || status;
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      'PENDING': 'Pendente',
      'PAID': 'Pago',
      'OVERDUE': 'Em Atraso',
      'CANCELLED': 'Cancelado',
      'REFUNDED': 'Reembolsado'
    };
    return labels[status] || status;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedReservations(reservations.map(r => r.id));
    } else {
      setSelectedReservations([]);
    }
  };

  const handleSelectReservation = (id, checked) => {
    if (checked) {
      setSelectedReservations([...selectedReservations, id]);
    } else {
      setSelectedReservations(selectedReservations.filter(rid => rid !== id));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Bulk Actions */}
      {selectedReservations.length > 0 && (
        <div className="bg-primary/10 border-b border-border p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              {selectedReservations.length} reserva(s) selecionada(s)
            </span>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" iconName="Mail">
                Enviar Email
              </Button>
              <Button variant="outline" size="sm" iconName="Download">
                Exportar
              </Button>
              <Button variant="destructive" size="sm" iconName="Trash2">
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop & Tablet Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 p-4">
                <input
                  type="checkbox"
                  checked={selectedReservations.length === reservations.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left p-4 font-semibold text-foreground cursor-pointer hover:bg-muted/80 transition-smooth"
                  onClick={() => handleSort('guestName')}>
                <div className="flex items-center space-x-2">
                  <span>Hóspede</span>
                  <Icon name="ArrowUpDown" size={14} color="var(--color-muted-foreground)" />
                </div>
              </th>
              <th className="text-left p-4 font-semibold text-foreground cursor-pointer hover:bg-muted/80 transition-smooth"
                  onClick={() => handleSort('roomNumber')}>
                <div className="flex items-center space-x-2">
                  <span>Quarto</span>
                  <Icon name="ArrowUpDown" size={14} color="var(--color-muted-foreground)" />
                </div>
              </th>
              <th className="text-left p-4 font-semibold text-foreground cursor-pointer hover:bg-muted/80 transition-smooth"
                  onClick={() => handleSort('checkInDate')}>
                <div className="flex items-center space-x-2">
                  <span>Check-in</span>
                  <Icon name="ArrowUpDown" size={14} color="var(--color-muted-foreground)" />
                </div>
              </th>
              <th className="text-left p-4 font-semibold text-foreground cursor-pointer hover:bg-muted/80 transition-smooth"
                  onClick={() => handleSort('checkOutDate')}>
                <div className="flex items-center space-x-2">
                  <span>Check-out</span>
                  <Icon name="ArrowUpDown" size={14} color="var(--color-muted-foreground)" />
                </div>
              </th>
              <th className="text-left p-4 font-semibold text-foreground cursor-pointer hover:bg-muted/80 transition-smooth"
                  onClick={() => handleSort('totalAmount')}>
                <div className="flex items-center space-x-2">
                  <span>Valor Total</span>
                  <Icon name="ArrowUpDown" size={14} color="var(--color-muted-foreground)" />
                </div>
              </th>
              <th className="text-left p-4 font-semibold text-foreground">Status</th>
              <th className="text-left p-4 font-semibold text-foreground">Pagamento</th>
              <th className="text-center p-4 font-semibold text-foreground">Ações</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(reservations) && reservations.length > 0 ? (
              reservations.map((reservation) => (
                <tr key={reservation.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedReservations.includes(reservation.id)}
                      onChange={(e) => handleSelectReservation(reservation.id, e.target.checked)}
                      className="rounded border-border"
                    />
                  </td>
                  <td className="p-4">
                    <div>
                      <div className="font-medium text-foreground">{reservation.guestName}</div>
                      <div className="text-sm text-muted-foreground">{reservation.guestEmail}</div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="Bed" size={16} color="var(--color-primary)" />
                      <span className="font-medium text-foreground">
                        {reservation.room?.number || reservation.roomNumber || 'N/A'}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {translateRoomType(reservation.room?.type || reservation.roomType) || 'N/A'}
                    </div>
                  </td>
                  <td className="p-4 text-foreground">{formatDate(reservation.checkInDate)}</td>
                  <td className="p-4 text-foreground">{formatDate(reservation.checkOutDate)}</td>
                  <td className="p-4 font-semibold text-foreground">
                    {(() => {
                      // Tratar discountAmount que pode ser null, "0" (string), ou número
                      let discount = 0;
                      if (reservation.discountAmount !== null && reservation.discountAmount !== undefined) {
                        discount = typeof reservation.discountAmount === 'string' 
                          ? parseFloat(reservation.discountAmount) 
                          : Number(reservation.discountAmount);
                      }
                      
                      const originalAmount = Number(reservation.totalAmount);
                      const finalAmount = originalAmount - discount;
                      
                      if (discount > 0) {
                        return (
                          <div>
                            <div className="text-success">{formatCurrency(finalAmount)}</div>
                            <div className="text-xs text-muted-foreground line-through">
                              {formatCurrency(originalAmount)}
                            </div>
                          </div>
                        );
                      }
                      return formatCurrency(originalAmount);
                    })()}
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reservation.status)}`}>
                      {getStatusLabel(reservation.status)}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(reservation.paymentStatus)}`}>
                      {getPaymentStatusLabel(reservation.paymentStatus)}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-center space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onViewDetails(reservation)}
                        title="Ver detalhes"
                      >
                        <Icon name="Eye" size={16} color="var(--color-muted-foreground)" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(reservation)}
                        title="Editar"
                      >
                        <Icon name="Edit" size={16} color="var(--color-muted-foreground)" />
                      </Button>
                      {(() => {
                        const statusUpper = reservation.status?.toUpperCase();
                        const canCheckIn = statusUpper === 'CONFIRMED' || statusUpper === 'PENDING';
                        return canCheckIn && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onCheckIn(reservation)}
                            title="Check-in (Confirmar chegada)"
                            disabled={loadingStates.checkIn?.[reservation.id]}
                          >
                            {loadingStates.checkIn?.[reservation.id] ? (
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Icon name="LogIn" size={16} color="var(--color-primary)" />
                            )}
                          </Button>
                        );
                      })()}
                      {(() => {
                        const statusUpper = reservation.status?.toUpperCase();
                        const shouldShowCheckout = statusUpper === 'PENDING' || statusUpper === 'CONFIRMED' || statusUpper === 'CHECKED_IN';
                        
                        return shouldShowCheckout && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onCheckOut(reservation)}
                            title="Checkout (Liberar quarto)"
                            disabled={loadingStates.checkOut?.[reservation.id]}
                          >
                            {loadingStates.checkOut?.[reservation.id] ? (
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Icon name="LogOut" size={16} color="var(--color-success)" />
                            )}
                          </Button>
                        );
                      })()}
                      {reservation.paymentStatus === 'PENDING' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onProcessPayment(reservation)}
                          title="Processar pagamento"
                          disabled={loadingStates.processPayment?.[reservation.id]}
                        >
                          {loadingStates.processPayment?.[reservation.id] ? (
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Icon name="CreditCard" size={16} color="var(--color-primary)" />
                          )}
                        </Button>
                      )}
                      {(() => {
                        const statusUpper = reservation.status?.toUpperCase();
                        const canCancel = statusUpper !== 'CANCELLED' && statusUpper !== 'CHECKED_OUT';
                        return canCancel && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onCancel?.(reservation)}
                            title="Cancelar reserva"
                            disabled={loadingStates.cancel?.[reservation.id]}
                          >
                            {loadingStates.cancel?.[reservation.id] ? (
                              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Icon name="XCircle" size={16} color="var(--color-destructive)" />
                            )}
                          </Button>
                        );
                      })()}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(reservation)}
                        title="Excluir"
                      >
                        <Icon name="Trash2" size={16} color="var(--color-destructive)" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="p-8 text-center text-muted-foreground">
                  <div className="flex flex-col items-center space-y-2">
                    <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" />
                    <p className="text-lg font-medium">Nenhuma reserva encontrada</p>
                    <p className="text-sm">Tente ajustar os filtros ou criar uma nova reserva.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden">
        {reservations.map((reservation) => (
          <div key={reservation.id} className="border-b border-border p-3 md:p-4 last:border-b-0">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedReservations.includes(reservation.id)}
                  onChange={(e) => handleSelectReservation(reservation.id, e.target.checked)}
                  className="rounded border-border"
                />
                <div>
                  <div className="font-medium text-foreground">{reservation.guestName}</div>
                  <div className="text-sm text-muted-foreground">{reservation.guestEmail}</div>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onViewDetails(reservation)}
                >
                  <Icon name="Eye" size={16} color="var(--color-muted-foreground)" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(reservation)}
                >
                  <Icon name="Edit" size={16} color="var(--color-muted-foreground)" />
                </Button>
                {(() => {
                  const statusUpper = reservation.status?.toUpperCase();
                  const canCheckIn = statusUpper === 'CONFIRMED' || statusUpper === 'PENDING';
                  return canCheckIn && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onCheckIn(reservation)}
                      title="Check-in (Confirmar chegada)"
                      disabled={loadingStates.checkIn?.[reservation.id]}
                    >
                      {loadingStates.checkIn?.[reservation.id] ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Icon name="LogIn" size={16} color="var(--color-primary)" />
                      )}
                    </Button>
                  );
                })()}
                {(() => {
                  const statusUpper = reservation.status?.toUpperCase();
                  const shouldShowCheckout = statusUpper === 'PENDING' || statusUpper === 'CONFIRMED' || statusUpper === 'CHECKED_IN';
                  
                  return shouldShowCheckout && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onCheckOut(reservation)}
                      title="Checkout (Liberar quarto)"
                      disabled={loadingStates.checkOut?.[reservation.id]}
                    >
                      {loadingStates.checkOut?.[reservation.id] ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Icon name="LogOut" size={16} color="var(--color-success)" />
                      )}
                    </Button>
                  );
                })()}
                {(() => {
                  const statusUpper = reservation.status?.toUpperCase();
                  const canCancel = statusUpper !== 'CANCELLED' && statusUpper !== 'CHECKED_OUT';
                  return canCancel && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onCancel?.(reservation)}
                      title="Cancelar reserva"
                      disabled={loadingStates.cancel?.[reservation.id]}
                    >
                      {loadingStates.cancel?.[reservation.id] ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Icon name="XCircle" size={16} color="var(--color-destructive)" />
                      )}
                    </Button>
                  );
                })()}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(reservation)}
                  title="Excluir"
                >
                  <Icon name="Trash2" size={16} color="var(--color-destructive)" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Quarto:</span>
                <div className="font-medium text-foreground">
                  {reservation.room?.number || reservation.roomNumber || 'N/A'} - {translateRoomType(reservation.room?.type || reservation.roomType) || 'N/A'}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Valor Total:</span>
                {(() => {
                  // Tratar discountAmount que pode ser null, "0" (string), ou número
                  let discount = 0;
                  if (reservation.discountAmount !== null && reservation.discountAmount !== undefined) {
                    discount = typeof reservation.discountAmount === 'string' 
                      ? parseFloat(reservation.discountAmount) 
                      : Number(reservation.discountAmount);
                  }
                  
                  const originalAmount = Number(reservation.totalAmount);
                  const finalAmount = originalAmount - discount;
                  
                  if (discount > 0) {
                    return (
                      <div>
                        <div className="font-medium text-success">{formatCurrency(finalAmount)}</div>
                        <div className="text-xs text-muted-foreground line-through">{formatCurrency(originalAmount)}</div>
                      </div>
                    );
                  }
                  return <div className="font-medium text-foreground">{formatCurrency(originalAmount)}</div>;
                })()}
              </div>
              <div>
                <span className="text-muted-foreground">Check-in:</span>
                <div className="font-medium text-foreground">{formatDate(reservation.checkInDate)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Check-out:</span>
                <div className="font-medium text-foreground">{formatDate(reservation.checkOutDate)}</div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(reservation.status)}`}>
                  {getStatusLabel(reservation.status)}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(reservation.paymentStatus)}`}>
                  {getPaymentStatusLabel(reservation.paymentStatus)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReservationTable;